import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import { Landmark, RefreshCw } from 'lucide-react';
import { formatIST } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../lib/api';

const BLUE = '#1d4ed8';
const SKY = '#0ea5e9';
const PIE_COLORS = [BLUE, SKY, '#60a5fa', '#93c5fd', '#0369a1', '#38bdf8'];

interface ZohoB2Props {
  onHover: (insight: HoverInsight | null) => void;
}

interface Summary {
  connected: boolean;
  synced_at: string | null;
  total_invoiced: number;
  invoices_count: number;
  outstanding_balance: number;
  outstanding_count: number;
  total_received: number;
  payments_count: number;
  total_expenses: number;
  expenses_count: number;
  invoice_status_counts: { name: string; count: number }[];
  expenses_by_account: { name: string; value: number }[];
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const fmtDate = (s: string) => { try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); } catch { return s || '—'; } };

function KpiCard({ label, value, note, accent }: { label: string; value: string; note: string; accent: string }) {
  return (
    <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm space-y-2" data-testid={`zoho-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="h-1.5 w-8 rounded-full" style={{ background: accent }} />
      <p className="text-2xl font-bold text-slate-800 font-mono tracking-tight">{value}</p>
      <p className="text-[10.5px] font-semibold text-slate-500">{label}</p>
      <p className="text-[9px] text-slate-400 font-mono">{note}</p>
    </div>
  );
}

function RecordsList({ title, testId, rows, columns, emptyText }: {
  title: string; testId: string; rows: any[]; emptyText: string;
  columns: { key: string; label: string; render?: (row: any) => React.ReactNode }[];
}) {
  return (
    <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">{title}</span>
        <span className="text-[10px] font-mono text-slate-400">{rows.length} of up to 50</span>
      </div>
      <div className="overflow-auto max-h-[360px] border border-slate-100 rounded-2xl">
        <table className="w-full text-left text-xs text-slate-600">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-slate-100 text-[10px] text-slate-400 font-mono font-bold">
              {columns.map((c) => <th key={c.key} className="py-3 px-4">{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-blue-50/40">
                {columns.map((c) => <td key={c.key} className="py-2.5 px-4 whitespace-nowrap">{c.render ? c.render(row) : (row[c.key] ?? '—')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-xs text-slate-400 font-mono p-6 text-center">{emptyText}</p>}
      </div>
    </div>
  );
}

const StatusBadge = ({ status }: { status: string }) => {
  const s = (status || '').toLowerCase();
  const paid = s === 'paid' || s === 'closed';
  return (
    <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-full border ${paid ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
      {(status || 'UNKNOWN').toUpperCase()}
    </span>
  );
};

export default function ZohoB2B({ onHover }: ZohoB2Props) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [outstanding, setOutstanding] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 89); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  const fetchAll = () => {
    const start_date = toISO(range.from);
    const end_date = toISO(range.to);
    setLoading(true);
    // Hits our own MongoDB (kept in sync from Zoho Books every 30 minutes in the background) —
    // changing the date range re-aggregates existing data, it never re-calls the Zoho Books API.
    Promise.allSettled([
      api.get('/finance/zoho/summary', { params: { start_date, end_date } }),
      api.get('/finance/zoho/records', { params: { entity: 'invoices', start_date, end_date, page: 1, limit: 50 } }),
      api.get('/finance/zoho/records', { params: { entity: 'payments', start_date, end_date, page: 1, limit: 50 } }),
      api.get('/finance/zoho/records', { params: { entity: 'outstanding_invoices', start_date, end_date, page: 1, limit: 50 } }),
      api.get('/finance/zoho/records', { params: { entity: 'expenses', start_date, end_date, page: 1, limit: 50 } }),
    ]).then(([s, inv, pay, out, exp]) => {
      if (s.status === 'fulfilled') setSummary(s.value.data);
      if (inv.status === 'fulfilled') setInvoices(inv.value.data.items || []);
      if (pay.status === 'fulfilled') setPayments(pay.value.data.items || []);
      if (out.status === 'fulfilled') setOutstanding(out.value.data.items || []);
      if (exp.status === 'fulfilled') setExpenses(exp.value.data.items || []);
    }).finally(() => setLoading(false));
  };

  useEffect(fetchAll, [range]);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await api.post('/finance/zoho/sync');
      // fire-and-forget on the backend — a full pull takes minutes, poll once shortly after
      setTimeout(fetchAll, 8000);
    } catch (e) {
      // connection badge staying stale surfaces the failure
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="finance-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            Finance Source of Truth
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Zoho Books Finance
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Live invoices, payments and expenses synced directly from Zoho Books every 30 minutes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="zoho" />
          <button
            data-testid="zoho-manual-sync-button"
            onClick={handleManualSync}
            disabled={syncing}
            className="px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs font-semibold border bg-white text-blue-700 border-blue-100 hover:border-blue-300 transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing…' : 'Manual Sync'}</span>
          </button>
          <div
            data-testid="zoho-connection-badge-finance"
            className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${
              summary?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'
            }`}
          >
            <Landmark className="h-4.5 w-4.5" />
            <span>{summary?.connected ? `Synced ${summary.synced_at ? formatIST(summary.synced_at) : ''}` : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div data-testid="zoho-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">
          Loading live Zoho Books data…
        </div>
      ) : !summary?.connected ? (
        <div data-testid="zoho-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          Zoho Books isn't connected yet. Ask your admin to click "Connect Zoho Books" in Admin Console → Integrations.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Total Invoiced" value={inr(summary.total_invoiced)} note={`${summary.invoices_count} invoices · selected window`} accent={BLUE} />
            <KpiCard label="Payments Received" value={inr(summary.total_received)} note={`${summary.payments_count} payments · selected window`} accent={SKY} />
            <KpiCard label="Outstanding Balance" value={inr(summary.outstanding_balance)} note={`${summary.outstanding_count} unpaid invoices · as of last sync`} accent="#d97706" />
            <KpiCard label="Total Expenses" value={inr(summary.total_expenses)} note={`${summary.expenses_count} expenses · selected window`} accent="#64748b" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Invoice Status Breakdown — Selected Window</span>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.invoice_status_counts} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} labelStyle={{ fontSize: '10px', color: '#1e293b', fontWeight: 'bold' }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Invoices">
                      {(summary.invoice_status_counts || []).map((_, i) => <Cell key={i} fill={i % 2 === 0 ? BLUE : SKY} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {summary.invoice_status_counts.length === 0 && <p className="text-xs text-slate-400 font-mono">No invoices found in this window.</p>}
            </div>
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Expenses by Account — Selected Window</span>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={summary.expenses_by_account} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {summary.expenses_by_account.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px', fontSize: '11px' }} formatter={(v: any) => inr(v)} />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {summary.expenses_by_account.length === 0 && <p className="text-xs text-slate-400 font-mono">No expenses found in this window.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RecordsList
              title="Recent Invoices" testId="zoho-list-invoices" rows={invoices} emptyText="No invoices found in this window."
              columns={[
                { key: 'invoice_number', label: 'Invoice #' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
                { key: 'total', label: 'Total', render: (r) => <span className="font-bold text-slate-800">{inr(r.total)}</span> },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              ]}
            />
            <RecordsList
              title="Payments Received" testId="zoho-list-payments" rows={payments} emptyText="No payments found in this window."
              columns={[
                { key: 'payment_number', label: 'Payment #' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
                { key: 'payment_mode', label: 'Mode' },
                { key: 'amount', label: 'Amount', render: (r) => <span className="font-bold text-blue-700">{inr(r.amount)}</span> },
              ]}
            />
            <RecordsList
              title="Outstanding Balances — As of Last Sync" testId="zoho-list-outstanding" rows={outstanding} emptyText="No outstanding invoices — fully paid up!"
              columns={[
                { key: 'invoice_number', label: 'Invoice #' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'due_date', label: 'Due Date', render: (r) => fmtDate(r.due_date) },
                { key: 'balance', label: 'Balance Owed', render: (r) => <span className="font-bold text-amber-700">{inr(r.balance)}</span> },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              ]}
            />
            <RecordsList
              title="Expenses" testId="zoho-list-expenses" rows={expenses} emptyText="No expenses found in this window."
              columns={[
                { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
                { key: 'account_name', label: 'Account' },
                { key: 'vendor_name', label: 'Vendor' },
                { key: 'description', label: 'Description' },
                { key: 'total', label: 'Amount', render: (r) => <span className="font-bold text-slate-800">{inr(r.total)}</span> },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
